import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import {
  ColumnDef,
  ColumnFiltersState,
  HeaderGroup,
  OnChangeFn,
  PaginationState,
  Row,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Download, X } from "lucide-react";
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";

import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/core/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/core/components/ui/table";

import { subscribeListPageReset } from "@/core/api/invalidate-after-mutation";
import { DEFAULT_PAGE_SIZE } from "@/core/api/pagination";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { cn } from "@/core/utils/func/cn";
import { notifSilenceWarning } from "@/core/utils/func/notifApp";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { EmptyTable } from "./empty-table";
import { ErrorTable } from "./error-table";
import { LoadingTable } from "./loading-table";

/**
 * Valeur sentinelle du filtre select « Tout » : sélectionner cette option
 * réinitialise le filtre de la colonne. Une option « Tout » est injectée
 * automatiquement en tête des options de chaque filtre select.
 */
export const SELECT_ALL_VALUE = "__all__";

/** Fabrique de colonnes typée : reçoit `navigate` et retourne les colonnes de la table. */
export type ColumnsClarFactory<T> = (
  navigate: NavigateFunction
) => ColumnDef<T>[];

type ColumnMetaType = {
  type: "date_from" | "date_end" | "date" | "select" | "text" | "denied";
  options?: { value: string; label: string }[];
  /** Placeholder de l’input texte ; sinon `filterByField` + libellé de colonne. */
  placeholder?: string;
};

interface DataTableProps<T> {
  data: T[];
  exportFileName: string;
  title: string;
  /** Masque le titre interne de la card (défaut : masqué). Le titre de page reste à la charge du parent. */
  hideTitle?: boolean;
  error?: {
    message: string;
    refetch?: () => void;
  };
  loading?: {
    isLoading: boolean;
    loaderComponent?: ReactNode;
    isFetching?: boolean;
  };
  itemPerPage?: number;
  columnsClar: ColumnsClarFactory<T>;
  enableSelection?: boolean;
  onSelectionChange?: (selectedIds: string[]) => void;
  /** Client: pagination + filtres sur les données déjà chargées. Server: une page par requête API. */
  paginationMode?: "client" | "server";
  /** Nombre total de pages renvoyé par l’API (mode server). */
  serverPageCount?: number;
  /** Total d’éléments toutes pages confondues pour le libellé (mode server). */
  totalItemsCount?: number;
  /** Pagination contrôlée par le parent (recommandé en mode server). */
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  /** Mode server : à fournir depuis le parent pour appliquer les filtres via l’API (React Query). */
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  /**
   * Mode server : appelé à chaque changement de tri pour que le parent trie
   * côté API (le tri local ne couvre que la page courante).
   */
  onSortingChange?: OnChangeFn<SortingState>;
  /** Masque le bouton « Réinitialiser les filtres » (défaut : affiché). */
  hideResetFilters?: boolean;
  /** Affiche le bouton d’export CSV (défaut : masqué). La permission est décidée par le parent. */
  canExport?: boolean;
  /**
   * Mode server : callback optionnel pour exporter la **liste complète**
   * (toutes les pages API, filtres actifs appliqués). Si fourni, un 3ème
   * bouton apparaît dans le dialog d'export. Le parent orchestre l'appel API
   * (boucle de pages à `MAX_PAGE_SIZE`) et renvoie l'ensemble des lignes.
   */
  fetchAllForExport?: () => Promise<T[]>;
}

interface RowDataType {
  id: string;
  // Ajoutez ici d'autres propriétés selon vos besoins
}

export const DataTableClar = <T extends RowDataType>({
  data,
  exportFileName,
  title,
  hideTitle = true,
  error,
  loading,
  itemPerPage,
  columnsClar,
  enableSelection = false,
  onSelectionChange,
  paginationMode = "server",
  serverPageCount,
  totalItemsCount,
  pagination: paginationControlled,
  onPaginationChange: onPaginationChangeControlled,
  columnFilters: columnFiltersControlled,
  onColumnFiltersChange: onColumnFiltersChangeControlled,
  onSortingChange: onSortingChangeProp,
  hideResetFilters = false,
  canExport = true,
  fetchAllForExport,
}: DataTableProps<T>) => {
  const navigate = useNavigate();
  const { t } = useTypedTranslation();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [internalColumnFilters, setInternalColumnFilters] =
    React.useState<ColumnFiltersState>([]);
  const isControlledColumnFilters =
    columnFiltersControlled !== undefined &&
    onColumnFiltersChangeControlled !== undefined;
  const effectiveColumnFilters = isControlledColumnFilters
    ? columnFiltersControlled!
    : internalColumnFilters;
  const handleColumnFiltersChange: OnChangeFn<ColumnFiltersState> =
    isControlledColumnFilters
      ? onColumnFiltersChangeControlled!
      : setInternalColumnFilters;

  const isPaginationControlled =
    paginationControlled !== undefined &&
    onPaginationChangeControlled !== undefined;

  useEffect(() => {
    if (
      import.meta.env.DEV &&
      paginationMode === "server" &&
      !isPaginationControlled
    ) {
      console.warn(
        'DataTableClar: paginationMode="server" attend pagination et onPaginationChange depuis le parent (requête API).'
      );
    }
  }, [paginationMode, isPaginationControlled]);

  useEffect(() => {
    if (
      import.meta.env.DEV &&
      paginationMode === "server" &&
      isPaginationControlled &&
      !isControlledColumnFilters
    ) {
      console.warn(
        'DataTableClar: paginationMode="server" avec pagination contrôlée : fournir columnFilters et onColumnFiltersChange pour filtrer via l’API (inopportun si la page filtre via une barre externe ou n’a aucune colonne filtrable).'
      );
    }
  }, [paginationMode, isPaginationControlled, isControlledColumnFilters]);

  useEffect(() => {
    if (
      import.meta.env.DEV &&
      paginationMode === "server" &&
      !onSortingChangeProp
    ) {
      console.warn(
        'DataTableClar: paginationMode="server" active manualSorting : sans onSortingChange, le tri ne s’applique qu’à la page courante. Fournir onSortingChange et trier côté serveur.'
      );
    }
  }, [paginationMode, onSortingChangeProp]);

  const [internalPagination, setInternalPagination] = useState<PaginationState>(
    () => ({
      pageIndex: 0,
      pageSize: itemPerPage ? itemPerPage : DEFAULT_PAGE_SIZE,
    })
  );

  const paginationState: PaginationState = isPaginationControlled
    ? paginationControlled!
    : internalPagination;

  const handlePaginationChange: OnChangeFn<PaginationState> = (updater) => {
    if (isPaginationControlled && onPaginationChangeControlled) {
      onPaginationChangeControlled(updater);
    } else {
      setInternalPagination(updater);
    }
  };

  const handlePaginationChangeRef = useRef(handlePaginationChange);
  handlePaginationChangeRef.current = handlePaginationChange;

  useEffect(() => {
    return subscribeListPageReset(() => {
      handlePaginationChangeRef.current((prev) =>
        prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
      );
    });
  }, []);

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting(updater);
    onSortingChangeProp?.(updater);
  };

  const pageIndex = paginationState.pageIndex;
  const pageSize = paginationState.pageSize;

  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [exportFormat, setExportFormat] = useState<"csv" | "xlsx">("csv");

  const goToPage = (page: number) => {
    handlePaginationChange({ pageIndex: page - 1, pageSize });
  };

  const isServerPagination = paginationMode === "server";

  const computedPageCount = useMemo(() => {
    if (isServerPagination) {
      return serverPageCount ?? 0;
    }
    if (data == null) return 1;
    const n = Math.ceil(data.length / pageSize) || 1;
    return n;
  }, [isServerPagination, serverPageCount, data, pageSize]);

  // Fonction optimisée pour créer les numéros de page (affiche seulement les pages pertinentes)
  const pageNumbers = useMemo(() => {
    const pageCount = computedPageCount;
    const currentPage = pageIndex + 1;
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5; // Nombre maximum de pages visibles autour de la page actuelle

    if (pageCount <= 0) {
      return pages;
    }

    if (pageCount <= maxVisiblePages + 2) {
      // Si peu de pages, afficher toutes
      for (let i = 1; i <= pageCount; i++) {
        pages.push(i);
      }
    } else {
      // Toujours afficher la première page
      pages.push(1);

      // Calculer le début et la fin de la plage autour de la page actuelle
      let start = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2));
      let end = Math.min(
        pageCount - 1,
        currentPage + Math.floor(maxVisiblePages / 2)
      );

      // Ajuster si on est proche du début ou de la fin
      if (currentPage <= Math.floor(maxVisiblePages / 2) + 1) {
        end = maxVisiblePages;
      } else if (currentPage >= pageCount - Math.floor(maxVisiblePages / 2)) {
        start = pageCount - maxVisiblePages + 1;
      }

      // Ajouter ellipsis avant si nécessaire
      if (start > 2) {
        pages.push("ellipsis-start");
      }

      // Ajouter les pages autour de la page actuelle
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Ajouter ellipsis après si nécessaire
      if (end < pageCount - 1) {
        pages.push("ellipsis-end");
      }

      // Toujours afficher la dernière page
      pages.push(pageCount);
    }

    return pages;
  }, [computedPageCount, pageIndex]);

  // --- Ajout dynamique de la colonne de sélection ---
  const columnsWithSelection = useMemo<ColumnDef<T>[]>(() => {
    try {
      const baseColumns = columnsClar(navigate);

      // Vérifier que les colonnes sont valides
      if (!Array.isArray(baseColumns)) {
        console.error("columnsClar must return an array");
        return [];
      }

      // Filtrer les colonnes invalides
      const validColumns = baseColumns.filter((col) => {
        if (!col || typeof col !== "object") {
          console.warn("Invalid column found:", col);
          return false;
        }
        // S'assurer que chaque colonne a au moins un id ou accessorKey
        if (!col.id && !("accessorKey" in col)) {
          console.warn("Column missing id or accessorKey:", col);
          return false;
        }
        return true;
      });

      if (!enableSelection) return validColumns;

      const selectionColumn: ColumnDef<T> = {
        id: "selection",
        header: "",
        cell: ({ row }) => (
          <input
            type="checkbox"
            title={t("selectRow")}
            aria-label={t("selectRow")}
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
            className="w-4 h-4 accent-primary"
          />
        ),
        size: 40,
        meta: { type: "denied" } as ColumnDef<T>["meta"],
        enableSorting: false,
        enableHiding: false,
      };

      return [selectionColumn, ...validColumns];
    } catch (error) {
      console.error("Error creating columns:", error);
      return [];
    }
  }, [enableSelection, columnsClar, navigate, t]);

  // Instanciation de la table APRÈS la définition des colonnes
  // S'assurer que columnsWithSelection est un tableau valide et non vide
  const validColumns = useMemo<ColumnDef<T>[]>(() => {
    if (
      !Array.isArray(columnsWithSelection) ||
      columnsWithSelection.length === 0
    ) {
      console.warn("No valid columns available, using empty array");
      return [];
    }
    // Vérifier que toutes les colonnes ont les propriétés requises
    return columnsWithSelection
      .map((col, index) => {
        if (!col) {
          console.warn(`Column at index ${index} is null or undefined`);
          return null;
        }
        // S'assurer que chaque colonne a un header (même vide)
        if (col.header === undefined) {
          col.header = (("accessorKey" in col && col.accessorKey) ||
            col.id ||
            "") as ColumnDef<T>["header"];
        }
        return col;
      })
      .filter((col): col is ColumnDef<T> => col !== null);
  }, [columnsWithSelection]);

  // Ne créer la table que si on a des colonnes valides
  const table = useReactTable<T>({
    data: data || [],
    columns: validColumns.length > 0 ? validColumns : [],
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    getCoreRowModel: getCoreRowModel(),
    ...(isServerPagination
      ? {
          manualPagination: true,
          manualFiltering: true,
          manualSorting: true,
          pageCount: computedPageCount,
        }
      : {
          getPaginationRowModel: getPaginationRowModel(),
          getFilteredRowModel: getFilteredRowModel(),
        }),
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: handlePaginationChange,
    state: {
      sorting,
      columnFilters: effectiveColumnFilters,
      columnVisibility,
      rowSelection,
      pagination: paginationState,
    },
    enableRowSelection: enableSelection,
  });

  // --- Gestion de la sélection multiple ---
  useEffect(() => {
    if (enableSelection && onSelectionChange) {
      // Récupère les IDs sélectionnés de façon typée
      const selectedIds = table
        .getSelectedRowModel()
        .rows.map((row) => row.original.id);
      // Utiliser une référence pour éviter les appels répétés avec les mêmes valeurs
      onSelectionChange(selectedIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection, enableSelection]);

  const skipColumnFiltersPageReset = useRef(true);
  useEffect(() => {
    if (!isServerPagination) {
      return;
    }
    if (skipColumnFiltersPageReset.current) {
      skipColumnFiltersPageReset.current = false;
      return;
    }
    // Mode server (contrôlé ou non) : un nouveau filtre doit relire la page 1,
    // sinon l’API renvoie une page vide alors que des résultats existent.
    handlePaginationChange((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- éviter re-exécution à chaque rendu (handlePaginationChange non mémoïsé)
  }, [effectiveColumnFilters, isServerPagination]);

  // Écrit un jeu de lignes au format choisi (CSV UTF-8 BOM ou XLSX) et
  // déclenche le téléchargement.
  const writeAndDownload = (
    rows: Record<string, unknown>[],
    suffix: string
  ): boolean => {
    if (!rows.length) {
      notifSilenceWarning(t("noDataToExport"));
      return false;
    }
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

    let blob: Blob;
    let extension: string;
    if (exportFormat === "xlsx") {
      const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      extension = "xlsx";
    } else {
      const csv = XLSX.utils.sheet_to_csv(worksheet);
      blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      extension = "csv";
    }

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${exportFileName}_${suffix}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  };

  // Page courante (mode server) ou dataset complet (mode client).
  const exportCurrent = () => {
    const suffix = isServerPagination ? `page_${pageIndex + 1}` : "complete";
    if (writeAndDownload(data as unknown as Record<string, unknown>[], suffix)) {
      setIsExportDialogOpen(false);
    }
  };

  // Vue filtrée : en mode server, c'est la page courante (filtres déjà
  // appliqués côté API) ; en mode client, c'est le résultat du filtrage local.
  const exportFiltered = () => {
    const rowModel = isServerPagination
      ? table.getRowModel()
      : table.getFilteredRowModel();
    const filteredData = rowModel.rows.map(
      (row) => row.original as Record<string, unknown>
    );
    const suffix = isServerPagination
      ? `filtered_page_${pageIndex + 1}`
      : "filtered";
    if (writeAndDownload(filteredData, suffix)) {
      setIsExportDialogOpen(false);
    }
  };

  // Mode server : télécharge toutes les pages via `fetchAllForExport` fourni
  // par le parent (il clôt sur sa requête API et réapplique les filtres).
  const exportAll = async () => {
    if (!fetchAllForExport) return;
    setIsExportingAll(true);
    try {
      const allRows = await fetchAllForExport();
      if (writeAndDownload(allRows as unknown as Record<string, unknown>[], "full")) {
        setIsExportDialogOpen(false);
      }
    } catch (err) {
      console.error("exportAll failed:", err);
      notifSilenceWarning(t("noDataToExport"));
    } finally {
      setIsExportingAll(false);
    }
  };

  const resetFilters = () => {
    table.resetColumnFilters();
    if (isServerPagination) {
      handlePaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
      if (enableSelection) {
        table.resetRowSelection();
      }
      return;
    }
    handlePaginationChange(() => ({
      pageIndex: 0,
      pageSize: itemPerPage ? itemPerPage : DEFAULT_PAGE_SIZE,
    }));
  };

  const renderFilter = (column: any) => {
    if (!column || !column.columnDef) return null;
    const columnMeta = column.columnDef.meta as ColumnMetaType;

    switch (columnMeta?.type) {
      // Range
      case "date_from": {
        const filterValue = column.getFilterValue() || {};
        return (
          <Input
            id={column.id}
            type="date"
            placeholder={t("startDatePlaceholder")}
            value={filterValue.from ?? ""}
            onChange={(event) => {
              const newFilter = { ...filterValue, from: event.target.value };
              column.setFilterValue(newFilter);
            }}
            className="w-full sm:max-w-sm"
          />
        );
      }
      case "date_end": {
        const filterValue = column.getFilterValue() || {};
        return (
          <Input
            id={column.id}
            type="date"
            placeholder={t("endDatePlaceholder")}
            value={filterValue.to ?? ""}
            onChange={(event) => {
              const newFilter = { ...filterValue, to: event.target.value };
              column.setFilterValue(newFilter);
            }}
            className="w-full sm:max-w-sm"
          />
        );
      }
      // Range
      case "date": {
        return (
          <Input
            id={column.id}
            type="date"
            placeholder={t("filterByDate")}
            value={(column.getFilterValue() as string) ?? ""}
            onChange={(event) => {
              column.setFilterValue(event.target.value);
            }}
            className="w-full sm:max-w-sm"
          />
        );
      }
      case "select": {
        const options = columnMeta.options || [];
        const selected = column.getFilterValue() as string | undefined;
        return (
          <Select
            onValueChange={(value) => {
              // "Tout" : sentinelle legacy conservée pour rétrocompatibilité — utiliser SELECT_ALL_VALUE
              if (value === SELECT_ALL_VALUE || value === "Tout") {
                column.setFilterValue(undefined);
              } else {
                column.setFilterValue(value);
              }
            }}
            value={selected || SELECT_ALL_VALUE}
          >
            <SelectTrigger id={column.id} className="w-full sm:w-[180px]">
              <SelectValue placeholder={t("selectFilterPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SELECT_ALL_VALUE}>
                {t("common.all")}
              </SelectItem>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }
      case "text": {
        const headerLabel =
          typeof column.columnDef?.header === "string"
            ? column.columnDef.header
            : "";
        return (
          <Input
            id={column.id}
            placeholder={
              columnMeta.placeholder ||
              t("filterByField", {
                field: headerLabel || String(column.id ?? ""),
              })
            }
            value={(column.getFilterValue() as string) ?? ""}
            onChange={(event) => {
              const next = event.target.value;
              column.setFilterValue(next ? next : undefined);
            }}
            className="w-full sm:max-w-sm"
            autoComplete="off"
          />
        );
      }
      case "denied": {
        return null;
      }
      default:
        return null;
    }
  };

  const numberOfColumns = table?.getAllFlatColumns().length || 5; // defaults to 5
  const numberOfRows = table?.getRowModel().rows.length;
  return (
    <Card className="overflow-hidden mt-1 min-w-0 shadow-lg hover:translate-y-0">
      {!hideTitle ? (
        <CardHeader className="px-4 sm:px-6">
          <CardTitle className="text-xl font-bold sm:text-2xl">
            {title}
          </CardTitle>
        </CardHeader>
      ) : null}
      <CardContent className="px-4 sm:px-6">
        <div className="w-full min-w-0">
          {/* render filter */}
          <div className="flex flex-wrap gap-3 items-end py-2">
            {table.getAllColumns().map((column: any) => {
              if (!column || !column.columnDef) return null;
              const columnMeta = column.columnDef.meta as ColumnMetaType;
              if (columnMeta?.type === "denied") return null;
              return (
                <div
                  key={column.id}
                  className="flex w-full min-w-0 flex-col sm:w-auto sm:min-w-[10rem]"
                >
                  <label
                    htmlFor={column.id}
                    className="text-xs font-semibold text-muted-foreground"
                  >
                    {typeof column.columnDef.header === "string"
                      ? column.columnDef.header
                      : typeof column.columnDef.header === "function"
                        ? flexRender(column.columnDef.header, {
                            column,
                            header: column,
                            table,
                          })
                        : String(column.columnDef.header || "")}
                  </label>
                  {renderFilter(column)}
                </div>
              );
            })}
          </div>
          {/* render filter */}

          <div className="flex flex-col gap-2 py-1 sm:flex-row sm:items-end sm:justify-end">
            {canExport ? (
              <Button
                onClick={() => setIsExportDialogOpen(true)}
                className="w-full sm:ml-2 sm:w-auto"
              >
                <Download className="mr-2 w-4 h-4" />
                {t("exportCsv")}
              </Button>
            ) : null}

            {!hideResetFilters ? (
              <Button
                onClick={resetFilters}
                variant="destructive"
                className="w-full sm:ml-2 sm:w-auto"
              >
                <X className="mr-2 w-4 h-4" />
                {t("resetFilters")}
              </Button>
            ) : null}
          </div>
          <div className="flex gap-0 justify-end items-end py-1">
            {data ? (
              <p className="text-xs font-medium text-muted-foreground">
                {t("itemsCount", {
                  total:
                    totalItemsCount !== undefined
                      ? totalItemsCount
                      : data.length,
                  pageSize: String(pageSize),
                })}
              </p>
            ) : (
              ""
            )}
          </div>

          <div className="rounded-md border border-border bg-background/50 text-foreground">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup: HeaderGroup<T>) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header: any) => {
                      // Vérifications supplémentaires pour éviter les erreurs
                      if (
                        !header ||
                        header.isPlaceholder ||
                        !header.column ||
                        !header.column.columnDef
                      ) {
                        return (
                          <TableHead
                            key={header?.id || Math.random()}
                            colSpan={header?.colSpan || 1}
                            className="text-center"
                          >
                            {null}
                          </TableHead>
                        );
                      }

                      try {
                        const headerValue = header.column.columnDef.header;
                        if (headerValue === undefined || headerValue === null) {
                          return (
                            <TableHead
                              key={header.id}
                              colSpan={header.colSpan}
                              className="text-center"
                            >
                              {null}
                            </TableHead>
                          );
                        }

                        return (
                          <TableHead
                            key={header.id}
                            colSpan={header.colSpan}
                            className="text-center"
                            style={{
                              width: header.getSize(),
                              maxWidth: header.getSize(),
                            }}
                          >
                            {flexRender(headerValue, header.getContext())}
                          </TableHead>
                        );
                      } catch (error) {
                        console.error("Error rendering header:", error, {
                          headerId: header?.id,
                          columnId: header?.column?.id,
                        });
                        return (
                          <TableHead
                            key={header?.id || Math.random()}
                            colSpan={header?.colSpan || 1}
                            className="text-center"
                          >
                            {null}
                          </TableHead>
                        );
                      }
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {loading?.isLoading === true ? (
                  <>
                    {loading?.loaderComponent || (
                      <LoadingTable numberOfColumns={numberOfColumns} />
                    )}
                  </>
                ) : error?.message ? (
                  <ErrorTable error={error} />
                ) : !numberOfRows ? (
                  <EmptyTable />
                ) : (
                  <>
                    {table.getRowModel().rows.map((row: Row<T>) => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map((cell: any) => (
                          <TableCell
                            key={cell.id}
                            style={{
                              width: cell.column.getSize(),
                              maxWidth: cell.column.getSize(),
                            }}
                          >
                            {!cell || !cell.column || !cell.column.columnDef
                              ? null
                              : (() => {
                                  try {
                                    return flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext()
                                    );
                                  } catch (error) {
                                    console.error(
                                      "Error rendering cell:",
                                      error
                                    );
                                    return null;
                                  }
                                })()}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
          </div>
          {/* Pagination optimisée avec numéros de pages */}
          {computedPageCount > 0 ? (
            <div className="flex overflow-x-auto flex-wrap gap-1 justify-center py-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-2 h-8 text-xs shrink-0"
              >
                {t("previous")}
              </Button>
              {pageNumbers.map((page, index) => {
                if (page === "ellipsis-start" || page === "ellipsis-end") {
                  return (
                    <span
                      key={`ellipsis-${index}`}
                      className="hidden px-2 py-1 text-xs text-muted-foreground sm:inline"
                    >
                      ...
                    </span>
                  );
                }
                const pageNumber = page as number;
                const isCurrent = pageNumber === pageIndex + 1;
                const isEdge =
                  pageNumber === 1 || pageNumber === computedPageCount;
                return (
                  <Button
                    key={page}
                    variant={isCurrent ? "default" : "outline"}
                    size="sm"
                    onClick={() => goToPage(pageNumber)}
                    className={cn(
                      "h-8 w-8 shrink-0 p-0 text-xs",
                      !isCurrent && !isEdge && "max-sm:hidden"
                    )}
                  >
                    {page}
                  </Button>
                );
              })}
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-2 h-8 text-xs shrink-0"
              >
                {t("next")}
              </Button>
            </div>
          ) : null}

          {/* Indicateur de chargement pour les rafraîchissements en arrière-plan */}
          <div className="flex justify-center items-center w-full">
            <div
              className={cn(
                "absolute flex items-center bottom-4 bg-primary px-4 py-1 text-primary-foreground text-md shadow-md rounded-xl overflow-hidden transform transition-all duration-300 ease-in-out",
                loading?.isFetching
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              )}
            >
              <span className="mr-2">
                <ButtonSpinner size="default" />
              </span>
              {t("loadingText")}
            </div>
          </div>
        </div>

        {/* Dialog pour choisir le type d'export */}
        {canExport ? (
          <Dialog
            open={isExportDialogOpen}
            onOpenChange={(open) => {
              if (!open && isExportingAll) return;
              setIsExportDialogOpen(open);
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("exportCsv")}</DialogTitle>
                <DialogDescription>
                  {isServerPagination
                    ? t("chooseExportTypeServer")
                    : t("chooseExportType")}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {t("exportFormat")}
                </Label>
                <RadioGroup
                  value={exportFormat}
                  onValueChange={(v) =>
                    setExportFormat(v as "csv" | "xlsx")
                  }
                  className="flex flex-row gap-6"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="csv" id="export-format-csv" />
                    <Label
                      htmlFor="export-format-csv"
                      className="text-sm font-normal"
                    >
                      {t("exportFormatCsv")}
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="xlsx" id="export-format-xlsx" />
                    <Label
                      htmlFor="export-format-xlsx"
                      className="text-sm font-normal"
                    >
                      {t("exportFormatXlsx")}
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <DialogFooter className="flex-col gap-2 sm:flex-row">
                <Button
                  onClick={exportCurrent}
                  className="w-full sm:w-auto"
                  variant="default"
                  disabled={isExportingAll}
                >
                  <Download className="mr-2 w-4 h-4" />
                  {isServerPagination
                    ? t("exportScope.currentPage")
                    : t("exportScope.allLoaded")}
                </Button>
                <Button
                  onClick={exportFiltered}
                  className="w-full sm:w-auto"
                  variant="outline"
                  disabled={isExportingAll}
                >
                  <Download className="mr-2 w-4 h-4" />
                  {isServerPagination
                    ? t("exportScope.currentPageFiltered")
                    : t("exportScope.filteredOnly")}
                </Button>
                {isServerPagination && fetchAllForExport ? (
                  <Button
                    onClick={() => void exportAll()}
                    className="w-full sm:w-auto"
                    variant="secondary"
                    disabled={isExportingAll}
                  >
                    {isExportingAll ? (
                      <ButtonSpinner size="default" />
                    ) : (
                      <Download className="mr-2 w-4 h-4" />
                    )}
                    {isExportingAll
                      ? t("exportScope.fullListPending")
                      : t("exportScope.fullList")}
                  </Button>
                ) : null}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : null}
      </CardContent>
    </Card>
  );
};
